import { useState } from 'react';
import { useTranslate } from 'ra-core';
import { Upload } from 'lucide-react';
import { BulkOperationDialog } from './BulkOperationDialog';
import {
  type CsvRecord,
  type GridRowValidator,
  type RowProcessor,
} from './types';
import { type ZodCsvSchema } from './bulkZodUtils';
import './dataGrid.css';
import { Button } from '../../components/ui/button';

export interface BulkOperationProps<T extends CsvRecord> {
  label: string;
  resource: string;
  validateRow?: GridRowValidator<T>;
  schema: ZodCsvSchema<T>;
  /**
   * Example values to be included in the exported CSV.
   */
  exampleValues?: Partial<T>[];
  validationRateLimitDelay?: number;
  processRow: RowProcessor<T>;
  processingRateLimitDelay?: number;
}

export function BulkOperation<T extends CsvRecord>({
  label,
  resource,
  validateRow,
  schema,
  exampleValues,
  validationRateLimitDelay = 0,
  processRow,
  processingRateLimitDelay = 0,
}: BulkOperationProps<T>) {
  const translate = useTranslate();

  // Ensure that the Dialog's state gets reset whenever the Dialog is closed
  const [dialogKey, setDialogKey] = useState(() => crypto.randomUUID());

  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <Button type="button" onClick={() => setDialogOpen(true)}>
        <Upload className="rotate-180" />
        {translate(label, { _: label })}
      </Button>
      <BulkOperationDialog
        key={dialogKey}
        label={label}
        resource={resource}
        open={dialogOpen}
        validationRateLimitDelay={validationRateLimitDelay}
        onClose={() => {
          setDialogOpen(false);

          // Reset the dialog's internal state
          setDialogKey(crypto.randomUUID());
        }}
        validateRow={validateRow}
        schema={schema}
        exampleValues={exampleValues}
        processRow={processRow}
        processingRateLimitDelay={processingRateLimitDelay}
      />
    </>
  );
}
